/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      this.loadDefaultCategory();
    }

    /**
     * Loads the default circle's collection on page load, so the circle shown as selected and
     * the products below it agree without the shopper having to click anything.
     */
    loadDefaultCategory() {
      if (!this.results) return;

      // ?category=<collection handle> opens that circle, so a single category can be linked
      // to directly. Anything unrecognised falls back to the default circle.
      const requested = new URLSearchParams(window.location.search).get('category');
      let item = null;
      if (requested) {
        item = [...document.querySelectorAll('.collection-categories__item')].find((el) => {
          const href = el.querySelector('a')?.getAttribute('href') || '';
          return href.split('?')[0].split('/').pop() === requested;
        });
      }
      if (!item) item = document.querySelector('.collection-categories__item[data-default-selected]');

      const link = item?.querySelector('a');
      if (!link) return;

      const url = new URL(link.href);
      if (url.pathname === window.location.pathname) return;

      this.showCollectionProducts(url, item);
    }

    /**
     * Puts the open category in the address bar, so the page can be linked to or shared with
     * that circle already open. The default circle needs no parameter.
     * @param {?Element} item - The selected circle, or null when cleared.
     */
    updateCategoryURL(item) {
      const params = new URLSearchParams(window.location.search);
      const handle = item?.querySelector('a')?.getAttribute('href')?.split('?')[0].split('/').pop();

      if (handle && !item.hasAttribute('data-default-selected')) {
        params.set('category', handle);
      } else {
        params.delete('category');
      }

      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query && '?'.concat(query)}`
      );
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

        // Add event listener for mobile active filters (below collection heading)
        const mobileActiveFilters = document.getElementById('mobile-active-filters');
        if (mobileActiveFilters) {
          mobileActiveFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        }

        // Add event listener for the circular category links (above the toolbar).
        // addListeners() runs again after every re-render, so only bind once.
        const categories = document.getElementById('collection-categories');
        if (categories && !categories.dataset.listenerBound) {
          categories.dataset.listenerBound = 'true';
          categories.addEventListener('click', this.handleCategoriesClick.bind(this));
        }
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      // Dont reload while the user is moving between the min/max price inputs, otherwise
      // re-rendering the form wipes the value they're part way through typing.
      const active = document.activeElement;
      if (evt.target.id?.includes('price-range-number')
        && active !== evt.target
        && active?.id?.includes('price-range-number')) return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            currentSortBy = evt.detail.selectedValue;
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles 'click' events on the circular category links.
     * Either way the circles stay put and only the grid below them changes: a link on this
     * collection goes through the filter path, a link to another collection loads that
     * collection's products in place. Clicking the selected circle restores the full set.
     * @param {object} evt - Event object.
     */
    handleCategoriesClick(evt) {
      const link = evt.target.closest('a');
      if (!link) return;

      const url = new URL(link.href);
      evt.preventDefault();

      if (url.pathname === window.location.pathname) {
        this.applyFilters(url.searchParams.toString(), evt);
        return;
      }

      const item = link.closest('.collection-categories__item');
      // The default circle starts out selected without anything being loaded, so the first
      // click loads its collection. Once something is loaded, it clears like any other.
      const isLoaded = this.activeCollectionPath || !item?.hasAttribute('data-default-selected');

      if (item && item.classList.contains('is-active') && isLoaded) {
        this.showBaseProducts();
      } else {
        this.showCollectionProducts(url, item);
      }
    }

    /**
     * Replaces the product grid with another collection's products, leaving the rest of the
     * page (and the circles) untouched.
     * @param {URL} url - The collection to load.
     * @param {Element} item - The circle to mark as selected.
     */
    async showCollectionProducts(url, item) {
      // Remember the grid this page was rendered with, so the circle can be cleared again.
      if (this.baseResultsHtml === undefined) this.baseResultsHtml = this.results.innerHTML;

      try {
        this.results.classList.add('is-loading');

        // Disable infinite scrolling: the pagination belongs to the host collection.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        if (this.categoriesFetchAbortController) {
          this.categoriesFetchAbortController.abort('Request changed');
        }
        this.categoriesFetchAbortController = new AbortController();

        const response = await fetch(url.href, {
          method: 'GET',
          signal: this.categoriesFetchAbortController.signal
        });
        if (!response.ok) return;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        const newResults = tmpl.content.getElementById('filter-results');
        if (!newResults) return;

        // Preserve the CSS class of the results, so the grid/list toggle is respected.
        const currentResultsUl = this.results.querySelector('ul');
        if (currentResultsUl) this.currentResultsClass = currentResultsUl.getAttribute('class');

        this.results.innerHTML = newResults.innerHTML;

        const newResultsUl = this.results.querySelector('ul');
        if (newResultsUl && this.currentResultsClass) {
          newResultsUl.setAttribute('class', this.currentResultsClass);
        }

        // Show that collection's own filters, and point later filtering at it.
        this.activeCollectionPath = url.pathname;
        this.syncFilterUi(tmpl.content);
        this.setSelectedCategory(item);
      } catch (error) {
        if (error !== 'Request changed') throw error;
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Restores the grid this page was rendered with and clears the selected circle.
     */
    showBaseProducts() {
      // With a default circle the row always has a selection, so clearing goes back to that
      // category rather than to this collection's own products, keeping the selected circle,
      // the grid and the filters describing the same thing.
      const defaultItem = document.querySelector('.collection-categories__item[data-default-selected]');
      const defaultLink = defaultItem?.querySelector('a');
      if (defaultLink && new URL(defaultLink.href).pathname !== window.location.pathname) {
        this.showCollectionProducts(new URL(defaultLink.href), defaultItem);
        return;
      }

      if (this.baseResultsHtml !== undefined) this.results.innerHTML = this.baseResultsHtml;

      // The grid belongs to this page again, so let infinite scroll resume.
      const customPagination = document.querySelector('custom-pagination');
      if (customPagination) {
        delete customPagination.dataset.pauseInfiniteScroll;
        if (customPagination.reload) customPagination.reload();
      }
      if (this.baseFacetsHtml !== undefined) {
        this.form.innerHTML = this.baseFacetsHtml;
        if (this.baseFilterSectionId === undefined) {
          delete this.form.dataset.filterSectionId;
        } else {
          this.form.dataset.filterSectionId = this.baseFilterSectionId;
        }
        this.addElements();
        this.addListeners();
      }
      this.activeCollectionPath = null;
      this.setSelectedCategory(null);
    }

    /**
     * Replaces the filter UI with the one belonging to a fetched collection, so the options
     * on offer match the products now in the grid.
     * @param {DocumentFragment} content - The fetched page.
     */
    syncFilterUi(content) {
      const newFacets = content.getElementById('facets');
      if (!this.form || !newFacets) return;

      // Remember this page's own filters, so clearing the circle can put them back.
      if (this.baseFacetsHtml === undefined) {
        this.baseFacetsHtml = this.form.innerHTML;
        this.baseFilterSectionId = this.form.dataset.filterSectionId;
      }

      this.form.innerHTML = newFacets.innerHTML;
      if (newFacets.dataset.filterSectionId) {
        this.form.dataset.filterSectionId = newFacets.dataset.filterSectionId;
      }

      const closeBtn = this.querySelector('.js-close-drawer-mob');
      const newCloseBtn = content.querySelector('.js-close-drawer-mob');
      if (closeBtn && newCloseBtn) closeBtn.innerText = newCloseBtn.innerText;

      const mobileActiveFilters = document.getElementById('mobile-active-filters');
      const newMobileActiveFilters = content.getElementById('mobile-active-filters');
      if (mobileActiveFilters && newMobileActiveFilters) {
        mobileActiveFilters.innerHTML = newMobileActiveFilters.innerHTML;
        mobileActiveFilters.hidden = newMobileActiveFilters.hidden;
      }

      // Rebind the re-rendered filter controls.
      this.addElements();
      this.addListeners();
    }

    /**
     * Marks one circle as selected, or none when passed null.
     * @param {?Element} item - The circle to select.
     */
    setSelectedCategory(item) {
      const selected = item || document.querySelector('.collection-categories__item[data-default-selected]');
      this.updateCategoryURL(selected);
      document.querySelectorAll('.collection-categories__item').forEach((el) => {
        el.classList.toggle('is-active', el === selected);
        el.querySelector('a')?.toggleAttribute('aria-current', el === selected);
      });
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible. While a category circle is
        // selected the grid belongs to that collection, so filter it rather than this page.
        let fetchUrl = `${this.activeCollectionPath || window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Update mobile active filters (shown below collection heading)
          const mobileActiveFilters = document.getElementById('mobile-active-filters');
          const newMobileActiveFilters = tmpl.content.getElementById('mobile-active-filters');
          if (mobileActiveFilters && newMobileActiveFilters) {
            mobileActiveFilters.innerHTML = newMobileActiveFilters.innerHTML;
            mobileActiveFilters.hidden = newMobileActiveFilters.hidden;
          }

          // Update the circular category links, so the selected circle follows the filters
          const categories = document.getElementById('collection-categories');
          const newCategories = tmpl.content.getElementById('collection-categories');
          if (categories && newCategories) {
            categories.innerHTML = newCategories.innerHTML;
          }

          // Update filter count badge in toolbar button
          const newToolbar = tmpl.content.getElementById('products-toolbar');
          if (newToolbar) {
            const newFilterBtn = newToolbar.querySelector('.products-toolbar__filter-btn');
            const currentFilterBtn = document.querySelector('.products-toolbar__filter-btn');
            const newBadge = newFilterBtn?.querySelector('.filter-count-badge');
            const currentBadge = currentFilterBtn?.querySelector('.filter-count-badge');
            
            if (newBadge && currentBadge) {
              const newCount = newBadge.dataset.filterCount;
              currentBadge.dataset.filterCount = newCount;
              currentBadge.textContent = `(${newCount})`;
              
              if (parseInt(newCount, 10) > 0) {
                currentBadge.classList.remove('hidden');
                currentFilterBtn.classList.add('has-active-filters');
              } else {
                currentBadge.classList.add('hidden');
                currentFilterBtn.classList.remove('has-active-filters');
              }
            }
          }

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      // Filtering rebuilds the query from the filter form, which does not know about the
      // open category, so carry that parameter across.
      const category = new URLSearchParams(window.location.search).get('category');
      const params = new URLSearchParams(searchParams);
      if (category) params.set('category', category);
      const query = params.toString();

      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${query && '?'.concat(query)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
